import React from 'react';
import type { NotificationOut } from 'service/notificationsService';
import type { KnownNotification } from './types';
import { hrisRenderers } from './hris';
import { surveyRenderers } from './survey';

type NotificationTypeMap = { [N in KnownNotification as N['type']]: N };

// Exhaustive over KnownNotification['type'], every type must have a renderer or this won't compile.
const registry: { [K in KnownNotification['type']]: (notification: NotificationTypeMap[K]) => React.ReactNode } = {
  ...hrisRenderers,
  ...surveyRenderers,
};

// Runtime list of registered types, derived from `registry` so it can't drift.
// Single source of truth for anything needing the known-type set (e.g. the dev
// notifications sandbox).
export const KNOWN_NOTIFICATION_TYPES = Object.keys(registry) as KnownNotification['type'][];

function isKnownNotification(n: NotificationOut): n is KnownNotification {
  return n.type in registry;
}

function callRegistry<K extends KnownNotification['type']>(
  key: K,
  notification: NotificationTypeMap[K]
): React.ReactNode {
  return registry[key](notification);
}

// Defers registry invocation to render time so a parent error boundary can contain throws.
function RegistryInvocation({ notification }: { notification: KnownNotification }) {
  return <>{callRegistry(notification.type, notification)}</>;
}

export function renderNotification(notification: NotificationOut): React.ReactNode | null {
  if (!isKnownNotification(notification)) {
    console.error(`Missing notification registry entry for type: ${notification.type}`);
    return null;
  }
  return <RegistryInvocation notification={notification} />;
}
